/**
 * Feature slugs constants for Librattum
 *
 * FONTE CANÔNICA: os slugs abaixo DEVEM ser idênticos aos gerados por
 * `backend/seed_features.py` (slug = slugify(name)). É a partir desses slugs que
 * o backend monta `enabled_feature_slugs` em `GET /users/me`, então qualquer
 * divergência faz o gating falhar silenciosamente (a feature fica bloqueada para
 * todos os planos, inclusive quem paga).
 *
 * Proteção contra regressão:
 *  - frontend: `src/config/features.test.ts`
 *  - backend:  `backend/tests/test_feature_slugs_canonical.py` (congela a lista canônica)
 */

// Dashboard Features
export const FEATURES_DASHBOARD = {
  DASHBOARD_FINANCEIRO: 'dashboard-financeiro',
  KPIS_FINANCEIROS: 'kpis-financeiros',
  PLANEJADO_X_REALIZADO: 'planejado-x-realizado',
  DESPESAS_POR_CATEGORIA: 'despesas-por-categoria',
  EVOLUCAO_DO_SALDO: 'evolucao-do-saldo',
  PROGRESSO_DE_METAS: 'progresso-de-metas',
  CALENDARIO_FINANCEIRO: 'calendario-financeiro',
  TRANSACOES_RECENTES: 'transacoes-recentes',
  ALERTAS_FINANCEIROS: 'alertas-financeiros',
  NAVEGACAO_DE_PERIODO: 'navegacao-de-periodo',
} as const;

// Revenues Features
export const FEATURES_REVENUES = {
  GESTAO_RECEITAS: 'gestao-de-receitas',
  RECEITAS_CONTINUAS: 'receitas-continuas',
  RECEITAS_UNICAS: 'receitas-unicas',
  RECEITAS_PARCELADAS: 'receitas-parceladas',
  RECEITAS_FRACIONADAS: 'receitas-fracionadas',
  REALIZACAO_DE_RECEITAS: 'realizacao-de-receitas',
  COPIA_DE_RECEITAS: 'copia-de-receitas',
} as const;

// Future Expenses Features
export const FEATURES_FUTURE_EXPENSES = {
  DESPESAS_FUTURAS: 'despesas-futuras',
  CALCULO_DE_INVESTIMENTO: 'calculo-de-investimento',
  TAXA_DE_JUROS_MENSAL: 'taxa-de-juros-mensal',
  DATA_ALVO: 'data-alvo',
  VALOR_PRESENTE: 'valor-presente',
  STATUS_DE_METAS: 'status-de-metas',
  REALIZACAO_DE_METAS: 'realizacao-de-metas',
  REPLANEJAMENTO_DE_METAS: 'replanejamento-de-metas',
} as const;

// Monthly Expenses Features
export const FEATURES_MONTHLY_EXPENSES = {
  DESPESAS_MENSAIS: 'despesas-mensais',
  CATEGORIAS_DE_DESPESAS: 'categorias-de-despesas',
  VENCIMENTO_DE_DESPESAS: 'vencimento-de-despesas',
  STATUS_DE_DESPESAS: 'status-de-despesas',
  DESPESAS_VENCIDAS: 'despesas-vencidas',
  DESPESAS_CONTINUAS: 'despesas-continuas',
  DESPESAS_PARCELADAS: 'despesas-parceladas',
  DESPESAS_UNICAS: 'despesas-unicas',
} as const;

// Daily Expenses Features
// Obs.: "Registro Rápido" NÃO é uma funcionalidade — é comportamento intrínseco
// do módulo (condição do sistema), disponível junto com 'despesas-do-dia-a-dia'.
export const FEATURES_DAILY_EXPENSES = {
  DESPESAS_DIA_A_DIA: 'despesas-do-dia-a-dia',
  COPIA_DESPESAS_DIA_A_DIA: 'copia-de-despesas-do-dia-a-dia',
} as const;

// Reports Features
export const FEATURES_REPORTS = {
  RELATORIOS_FINANCEIROS: 'relatorios-financeiros',
  RESUMO_POR_CATEGORIA: 'resumo-por-categoria',
  COMPARATIVO_DE_PERIODOS: 'comparativo-de-periodos',
} as const;

// Relatórios — Abas
export const FEATURES_REPORTS_TABS = {
  ABA_RECEITAS: 'relatorios-aba-receitas',
  ABA_DESPESAS: 'relatorios-aba-despesas',
  ABA_TENDENCIAS: 'relatorios-aba-tendencias',
  ABA_RESUMO: 'relatorios-aba-resumo',
  ABA_FLUXO_DE_CAIXA: 'relatorios-aba-fluxo-de-caixa',
} as const;

// Relatórios — Gráficos
export const FEATURES_REPORTS_CHARTS = {
  LINHA_TEMPO_RECEITAS: 'relatorios-grafico-linha-tempo-receitas',
  RECEITAS_POR_TIPO: 'relatorios-grafico-receitas-por-tipo',
  LINHA_TEMPO_DESPESAS: 'relatorios-grafico-linha-tempo-despesas',
  GRAFICO_DESPESAS_POR_CATEGORIA: 'relatorios-grafico-despesas-por-categoria',
  SINTESE_DESPESAS_FUTURAS: 'relatorios-grafico-sintese-despesas-futuras',
  PROJECAO_DESPESAS_FUTURAS: 'relatorios-grafico-projecao-despesas-futuras',
  PLANEJADO_VS_REALIZADO: 'relatorios-grafico-planejado-vs-realizado',
  EVOLUCAO_TEMPO: 'relatorios-grafico-evolucao-tempo',
  DESPESAS_DENTRO_RECEITA: 'relatorios-grafico-despesas-dentro-receita',
  COMPARATIVO_RECEITA_DESPESA: 'relatorios-grafico-comparativo-receita-despesa',
  MEIOS_PAGAMENTO: 'relatorios-grafico-meios-pagamento',
  ANALISE_AGENTE: 'relatorios-grafico-analise-agente',
  FLUXO_DE_CAIXA: 'relatorios-grafico-fluxo-de-caixa',
} as const;

// Agents Features
export const FEATURES_AGENTS = {
  GESTAO_AGENTES: 'agentes',
  TIPOS_DE_AGENTE: 'tipos-de-agente',
} as const;

// Learning Features
export const FEATURES_LEARNING = {
  MATERIAIS_APOIO: 'materiais-de-apoio',
  VIDEO_AULAS: 'video-aulas',
  BLOG: 'blog',
} as const;

// Account Features
export const FEATURES_ACCOUNT = {
  MINHA_CONTA: 'minha-conta',
  PERFIL_USUARIO: 'perfil-do-usuario',
  SEGURANCA: 'seguranca',
  PREFERENCIAS: 'preferencias',
  TEMA_ESCURO: 'tema-escuro',
  MENTORIA_LIBRATTUM: 'mentoria-librattum',
} as const;

// Support Features
export const FEATURES_SUPPORT = {
  SUPORTE: 'suporte',
  TICKETS_SUPORTE: 'tickets-de-suporte',
  CENTRAL_AJUDA: 'central-de-ajuda',
} as const;

// System Features
export const FEATURES_SYSTEM = {
  PERIODOS_ORCAMENTARIOS: 'periodos-orcamentarios',
  LISTAS_FINANCEIRAS: 'listas-financeiras',
  NOTIFICACOES: 'notificacoes',
  EXPORTACAO_DADOS: 'exportacao-de-dados',
} as const;

// All features combined
export const ALL_FEATURES = {
  ...FEATURES_DASHBOARD,
  ...FEATURES_REVENUES,
  ...FEATURES_FUTURE_EXPENSES,
  ...FEATURES_MONTHLY_EXPENSES,
  ...FEATURES_DAILY_EXPENSES,
  ...FEATURES_REPORTS,
  ...FEATURES_REPORTS_TABS,
  ...FEATURES_REPORTS_CHARTS,
  ...FEATURES_AGENTS,
  ...FEATURES_LEARNING,
  ...FEATURES_ACCOUNT,
  ...FEATURES_SUPPORT,
  ...FEATURES_SYSTEM,
} as const;

// Type for feature slugs
export type FeatureSlug = typeof ALL_FEATURES[keyof typeof ALL_FEATURES];

/**
 * Lista canônica completa dos slugs (54). Usada por testes de consistência e
 * para validar, em runtime/dev, que um slug passado ao gating realmente existe.
 */
export const ALL_FEATURE_SLUGS: FeatureSlug[] = Object.values(ALL_FEATURES);

/** Verifica se uma string é um slug de funcionalidade reconhecido. */
export function isKnownFeatureSlug(slug: string): slug is FeatureSlug {
  return (ALL_FEATURE_SLUGS as string[]).includes(slug);
}

/**
 * Nome amigável (PT-BR) de cada funcionalidade — idêntico ao `name` do backend.
 * Usado no cadeado/`FeatureBlockedMessage` para exibir o nome correto da feature.
 */
export const FEATURE_NAMES: Record<FeatureSlug, string> = {
  [FEATURES_DASHBOARD.DASHBOARD_FINANCEIRO]: 'Dashboard Financeiro',
  [FEATURES_DASHBOARD.KPIS_FINANCEIROS]: 'KPIs Financeiros',
  [FEATURES_DASHBOARD.PLANEJADO_X_REALIZADO]: 'Planejado x Realizado',
  [FEATURES_DASHBOARD.DESPESAS_POR_CATEGORIA]: 'Despesas por Categoria',
  [FEATURES_DASHBOARD.EVOLUCAO_DO_SALDO]: 'Evolução do Saldo',
  [FEATURES_DASHBOARD.PROGRESSO_DE_METAS]: 'Progresso de Metas',
  [FEATURES_DASHBOARD.CALENDARIO_FINANCEIRO]: 'Calendário Financeiro',
  [FEATURES_DASHBOARD.TRANSACOES_RECENTES]: 'Transações Recentes',
  [FEATURES_DASHBOARD.ALERTAS_FINANCEIROS]: 'Alertas Financeiros',
  [FEATURES_DASHBOARD.NAVEGACAO_DE_PERIODO]: 'Navegação de Período',
  [FEATURES_REVENUES.GESTAO_RECEITAS]: 'Gestão de Receitas',
  [FEATURES_REVENUES.RECEITAS_CONTINUAS]: 'Receitas Contínuas',
  [FEATURES_REVENUES.RECEITAS_UNICAS]: 'Receitas Únicas',
  [FEATURES_REVENUES.RECEITAS_PARCELADAS]: 'Receitas Parceladas',
  [FEATURES_REVENUES.RECEITAS_FRACIONADAS]: 'Receitas Fracionadas',
  [FEATURES_REVENUES.REALIZACAO_DE_RECEITAS]: 'Realização de Receitas',
  [FEATURES_REVENUES.COPIA_DE_RECEITAS]: 'Cópia de Receitas',
  [FEATURES_FUTURE_EXPENSES.DESPESAS_FUTURAS]: 'Despesas Futuras',
  [FEATURES_FUTURE_EXPENSES.CALCULO_DE_INVESTIMENTO]: 'Cálculo de Investimento',
  [FEATURES_FUTURE_EXPENSES.TAXA_DE_JUROS_MENSAL]: 'Taxa de Juros Mensal',
  [FEATURES_FUTURE_EXPENSES.DATA_ALVO]: 'Data Alvo',
  [FEATURES_FUTURE_EXPENSES.VALOR_PRESENTE]: 'Valor Presente',
  [FEATURES_FUTURE_EXPENSES.STATUS_DE_METAS]: 'Status de Metas',
  [FEATURES_FUTURE_EXPENSES.REALIZACAO_DE_METAS]: 'Realização de Metas',
  [FEATURES_FUTURE_EXPENSES.REPLANEJAMENTO_DE_METAS]: 'Replanejamento de Metas',
  [FEATURES_MONTHLY_EXPENSES.DESPESAS_MENSAIS]: 'Despesas Mensais',
  [FEATURES_MONTHLY_EXPENSES.CATEGORIAS_DE_DESPESAS]: 'Categorias de Despesas',
  [FEATURES_MONTHLY_EXPENSES.VENCIMENTO_DE_DESPESAS]: 'Vencimento de Despesas',
  [FEATURES_MONTHLY_EXPENSES.STATUS_DE_DESPESAS]: 'Status de Despesas',
  [FEATURES_MONTHLY_EXPENSES.DESPESAS_VENCIDAS]: 'Despesas Vencidas',
  [FEATURES_MONTHLY_EXPENSES.DESPESAS_CONTINUAS]: 'Despesas Contínuas',
  [FEATURES_MONTHLY_EXPENSES.DESPESAS_PARCELADAS]: 'Despesas Parceladas',
  [FEATURES_MONTHLY_EXPENSES.DESPESAS_UNICAS]: 'Despesas Únicas',
  [FEATURES_DAILY_EXPENSES.DESPESAS_DIA_A_DIA]: 'Despesas do Dia a Dia',
  [FEATURES_DAILY_EXPENSES.COPIA_DESPESAS_DIA_A_DIA]: 'Cópia de Despesas do Dia a Dia',
  [FEATURES_REPORTS.RELATORIOS_FINANCEIROS]: 'Relatórios Financeiros',
  [FEATURES_REPORTS.RESUMO_POR_CATEGORIA]: 'Resumo por Categoria',
  [FEATURES_REPORTS.COMPARATIVO_DE_PERIODOS]: 'Comparativo de Períodos',
  [FEATURES_REPORTS_TABS.ABA_RECEITAS]: 'Relatórios — Aba Receitas',
  [FEATURES_REPORTS_TABS.ABA_DESPESAS]: 'Relatórios — Aba Despesas',
  [FEATURES_REPORTS_TABS.ABA_TENDENCIAS]: 'Relatórios — Aba Tendências',
  [FEATURES_REPORTS_TABS.ABA_RESUMO]: 'Relatórios — Aba Resumo',
  [FEATURES_REPORTS_TABS.ABA_FLUXO_DE_CAIXA]: 'Relatórios — Aba Fluxo de Caixa',
  [FEATURES_REPORTS_CHARTS.LINHA_TEMPO_RECEITAS]: 'Relatórios — Linha do Tempo de Receitas por Tipo',
  [FEATURES_REPORTS_CHARTS.RECEITAS_POR_TIPO]: 'Relatórios — Receitas por Tipo',
  [FEATURES_REPORTS_CHARTS.LINHA_TEMPO_DESPESAS]: 'Relatórios — Linha do Tempo de Despesas por Categoria',
  [FEATURES_REPORTS_CHARTS.GRAFICO_DESPESAS_POR_CATEGORIA]: 'Relatórios — Despesas por Categoria (gráfico)',
  [FEATURES_REPORTS_CHARTS.SINTESE_DESPESAS_FUTURAS]: 'Relatórios — Síntese de Despesas Futuras',
  [FEATURES_REPORTS_CHARTS.PROJECAO_DESPESAS_FUTURAS]: 'Relatórios — Projeção de Despesas Futuras',
  [FEATURES_REPORTS_CHARTS.PLANEJADO_VS_REALIZADO]: 'Relatórios — Planejado vs Realizado',
  [FEATURES_REPORTS_CHARTS.EVOLUCAO_TEMPO]: 'Relatórios — Evolução ao Longo do Tempo',
  [FEATURES_REPORTS_CHARTS.DESPESAS_DENTRO_RECEITA]: 'Relatórios — Despesas Dentro da Receita',
  [FEATURES_REPORTS_CHARTS.COMPARATIVO_RECEITA_DESPESA]: 'Relatórios — Comparativo Receita x Despesa',
  [FEATURES_REPORTS_CHARTS.MEIOS_PAGAMENTO]: 'Relatórios — Meios de Pagamento',
  [FEATURES_REPORTS_CHARTS.ANALISE_AGENTE]: 'Relatórios — Análise por Agente',
  [FEATURES_REPORTS_CHARTS.FLUXO_DE_CAIXA]: 'Relatórios — Fluxo de Caixa (gráfico)',
  [FEATURES_AGENTS.GESTAO_AGENTES]: 'Agentes',
  [FEATURES_AGENTS.TIPOS_DE_AGENTE]: 'Tipos de Agente',
  [FEATURES_LEARNING.MATERIAIS_APOIO]: 'Materiais de Apoio',
  [FEATURES_LEARNING.VIDEO_AULAS]: 'Vídeo-aulas',
  [FEATURES_LEARNING.BLOG]: 'Blog',
  [FEATURES_ACCOUNT.MINHA_CONTA]: 'Minha Conta',
  [FEATURES_ACCOUNT.PERFIL_USUARIO]: 'Perfil do Usuário',
  [FEATURES_ACCOUNT.SEGURANCA]: 'Segurança',
  [FEATURES_ACCOUNT.PREFERENCIAS]: 'Preferências',
  [FEATURES_ACCOUNT.TEMA_ESCURO]: 'Tema Escuro',
  [FEATURES_ACCOUNT.MENTORIA_LIBRATTUM]: 'Mentoria Librattum',
  [FEATURES_SUPPORT.SUPORTE]: 'Suporte',
  [FEATURES_SUPPORT.TICKETS_SUPORTE]: 'Tickets de Suporte',
  [FEATURES_SUPPORT.CENTRAL_AJUDA]: 'Central de Ajuda',
  [FEATURES_SYSTEM.PERIODOS_ORCAMENTARIOS]: 'Períodos Orçamentários',
  [FEATURES_SYSTEM.LISTAS_FINANCEIRAS]: 'Listas Financeiras',
  [FEATURES_SYSTEM.NOTIFICACOES]: 'Notificações',
  [FEATURES_SYSTEM.EXPORTACAO_DADOS]: 'Exportação de Dados',
};

/**
 * Gating por TIPO de lançamento. Mapeia o valor do tipo (enum do backend) ao
 * slug que o libera. Tipos ausentes do mapa são sempre liberados — é o caso de
 * `single` (Única), a base disponível em qualquer plano.
 *
 * Espelha `EXPENSE_TYPE_FEATURE_SLUGS` / `REVENUE_TYPE_FEATURE_SLUGS` em
 * `backend/core/deps.py` — back e front precisam concordar sobre o que bloquear.
 */
export const EXPENSE_TYPE_FEATURE: Partial<Record<string, FeatureSlug>> = {
  continuous: FEATURES_MONTHLY_EXPENSES.DESPESAS_CONTINUAS,
  installment: FEATURES_MONTHLY_EXPENSES.DESPESAS_PARCELADAS,
  // single -> sempre liberado (sem slug)
};

export const REVENUE_TYPE_FEATURE: Partial<Record<string, FeatureSlug>> = {
  continuous: FEATURES_REVENUES.RECEITAS_CONTINUAS,
  installment: FEATURES_REVENUES.RECEITAS_PARCELADAS,
  fractional: FEATURES_REVENUES.RECEITAS_FRACIONADAS,
  // single -> sempre liberado (sem slug)
};

/**
 * Mensagem exibida ao tentar selecionar um tipo bloqueado pelo plano.
 *
 * Texto fixo citando os planos que incluem esses tipos — o frontend só conhece
 * o próprio plano (via `enabled_feature_slugs`), não a lista de planos que
 * incluem uma feature; por isso os nomes são copy editável aqui.
 */
/**
 * Mensagem padrão de bloqueio por plano. Usada de forma consistente em todos os
 * pontos onde uma funcionalidade não incluída no plano é apenas informada
 * (tooltip de hover e toast) — sem redirecionar o usuário para a tela de planos.
 */
export const FEATURE_UNAVAILABLE_MESSAGE =
  'Esta funcionalidade não está disponível no seu plano.';

export const LOCKED_EXPENSE_TYPE_MESSAGE = FEATURE_UNAVAILABLE_MESSAGE;
export const LOCKED_REVENUE_TYPE_MESSAGE = FEATURE_UNAVAILABLE_MESSAGE;

/**
 * Map routes to required features.
 *
 * Observação: `/account` (Minha Conta) e `/account/budget-period` NÃO entram
 * aqui de propósito — são sempre acessíveis (é onde o usuário faz upgrade do
 * plano; bloquear criaria um beco sem saída). `periodos-orcamentarios` e
 * `minha-conta` são funcionalidades estruturais, incluídas em todos os planos.
 */
export const ROUTE_FEATURE_MAP: Record<string, FeatureSlug[]> = {
  '/financial': [FEATURES_DASHBOARD.DASHBOARD_FINANCEIRO],
  '/dashboard': [FEATURES_DASHBOARD.DASHBOARD_FINANCEIRO],
  '/financial/dashboard': [FEATURES_DASHBOARD.DASHBOARD_FINANCEIRO],
  '/financial/revenues': [FEATURES_REVENUES.GESTAO_RECEITAS],
  '/financial/future-expenses': [FEATURES_FUTURE_EXPENSES.DESPESAS_FUTURAS],
  '/financial/monthly-expenses': [FEATURES_MONTHLY_EXPENSES.DESPESAS_MENSAIS],
  '/financial/daily-expenses': [FEATURES_DAILY_EXPENSES.DESPESAS_DIA_A_DIA],
  '/financial/reports': [FEATURES_REPORTS.RELATORIOS_FINANCEIROS],
  '/financial/agents': [FEATURES_AGENTS.GESTAO_AGENTES],
  '/learn/materials': [FEATURES_LEARNING.MATERIAIS_APOIO],
  '/learn/videos': [FEATURES_LEARNING.VIDEO_AULAS],
  '/learn/blog': [FEATURES_LEARNING.BLOG],
  '/account/mentoria': [FEATURES_ACCOUNT.MENTORIA_LIBRATTUM],
  '/support': [FEATURES_SUPPORT.SUPORTE],
};

// Map sidebar items to features
export const SIDEBAR_ITEM_FEATURES: Record<string, FeatureSlug> = {
  'financial-dashboard': FEATURES_DASHBOARD.DASHBOARD_FINANCEIRO,
  'revenues': FEATURES_REVENUES.GESTAO_RECEITAS,
  'future-expenses': FEATURES_FUTURE_EXPENSES.DESPESAS_FUTURAS,
  'monthly-expenses': FEATURES_MONTHLY_EXPENSES.DESPESAS_MENSAIS,
  'daily-expenses': FEATURES_DAILY_EXPENSES.DESPESAS_DIA_A_DIA,
  'reports': FEATURES_REPORTS.RELATORIOS_FINANCEIROS,
  'materials': FEATURES_LEARNING.MATERIAIS_APOIO,
  'videos': FEATURES_LEARNING.VIDEO_AULAS,
  'blog': FEATURES_LEARNING.BLOG,
  'agents': FEATURES_AGENTS.GESTAO_AGENTES,
  'mentoria': FEATURES_ACCOUNT.MENTORIA_LIBRATTUM,
  'support': FEATURES_SUPPORT.SUPORTE,
};
